import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { resolveApiBase } from '../api.js';

/**
 * Invite-accept landing page.
 *
 * Reached via `/join?code=<invite>`.  The user should already be
 * signed in via Clerk (the main Protected wrapper around the Shell
 * covers that).  We grab their JWT, hand it to the public invite
 * endpoint, and bounce to the tenant home on success.
 */

export function Join() {
  const [params] = useSearchParams();
  const code = params.get('code') || '';
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const [state, setState] = useState(code ? 'accepting' : 'missing');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    (async () => {
      try {
        const token = await getToken();
        // Join pre-dates membership, so we can't use the shared client
        // (which sends X-Spotlight-Tenant-Id for a tenant the caller
        // doesn't belong to yet).  Raw fetch with the platform JWT is
        // fine — the public invite endpoint authenticates on the code.
        const apiBase = resolveApiBase();
        const resp = await fetch(`${apiBase}/v1/invites/accept`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Invitee-Email': user?.primaryEmailAddress?.emailAddress || '',
          },
          body: JSON.stringify({
            invite_code: code,
            display_name: user?.fullName || '',
          }),
        });
        if (!resp.ok) {
          // Extract the friendly detail from the API envelope rather
          // than dumping the raw JSON at the user — the envelope
          // includes request_id / server_time / circuit_breaker that
          // mean nothing to them and look like a system crash.
          let friendly = '';
          try {
            const body = await resp.json();
            friendly = body?.error?.detail || body?.detail || '';
          } catch { /* non-JSON body */ }
          if (!friendly) {
            // Last-ditch — map common HTTP codes to plain English.
            friendly = {
              401: 'Sign in first, then click the invite link again.',
              403: 'That invite is for a different email address.',
              404: 'Invite not found. Ask whoever invited you to resend.',
              410: 'That invite has already been used or has expired.',
            }[resp.status] || `Something went wrong (HTTP ${resp.status}).`;
          }
          const err = new Error(friendly);
          err.status = resp.status;
          throw err;
        }
        const data = await resp.json();
        if (!alive) return;
        setResult(data && 'data' in data ? data.data : data);
        setState('ok');
        setTimeout(() => navigate('/', { replace: true }), 1600);
      } catch (e) {
        if (alive) {
          setErr(e.message);
          setState('error');
        }
      }
    })();
    return () => { alive = false; };
  }, [code, getToken, navigate, user]);

  return (
    <div className="join-container">
      <div className="card join-card">
        <h1>Join a tenant</h1>
        {state === 'missing' && (
          <p>
            The invite link is missing its code. Ask whoever invited you to
            resend — the full URL should end with <code>?code=…</code>.
          </p>
        )}
        {state === 'accepting' && <p>Joining your new tenant…</p>}
        {state === 'ok' && result && (
          <p>
            Welcome. You've joined <code>{result.tenant_id}</code> as{' '}
            <strong>{result.role}</strong>. Redirecting…
          </p>
        )}
        {state === 'error' && (
          <div className="state state--error">
            <div className="state__title">Couldn't join</div>
            <div>{err}</div>
          </div>
        )}
      </div>
      <style>{`
        .join-container {
          min-height: 100vh;
          display: grid;
          place-items: center;
          padding: 40px 20px;
          background: var(--surface-1);
        }
        .join-card {
          max-width: 480px;
          width: 100%;
        }
      `}</style>
    </div>
  );
}
